import { HTTPError } from "ky";
import { z } from "zod";

const VALIDATION_STATUSES = new Set([400, 409, 412, 422]);

const SERVICE_UNAVAILABLE_STATUSES = new Set([429, 500, 502, 503, 504]);

export const UPDATE_SHIPMENT_DETAILS_ERROR_MESSAGES = {
	validation:
		"We could not update your FTL quote. Please review the shipment details and try again.",
	serviceUnavailable:
		"We could not update your FTL quote right now. Please try again in a few minutes.",
	fallback: "We could not update your FTL quote. Please try again.",
	persistentFailureGuidance: "If the problem continues, contact support.",
} as const;

function withPersistentFailureGuidance(
	message: string,
	consecutiveFailureCount?: number,
): string {
	if ((consecutiveFailureCount ?? 0) <= 3) {
		return message;
	}

	return `${message} ${UPDATE_SHIPMENT_DETAILS_ERROR_MESSAGES.persistentFailureGuidance}`;
}

type ErrorWithStatus = {
	response?: {
		status?: unknown;
	};
};

function getHttpStatus(error: unknown): number | null {
	if (!error || typeof error !== "object") {
		return null;
	}

	const typedError = error as ErrorWithStatus;
	if (!typedError.response || typeof typedError.response !== "object") {
		return null;
	}

	const { status } = typedError.response;
	return typeof status === "number" ? status : null;
}

function isNetworkOrTransportError(error: unknown): boolean {
	if (!(error instanceof Error)) {
		return false;
	}

	const normalizedMessage = error.message.toLowerCase();
	if (
		normalizedMessage.includes("failed to fetch") ||
		normalizedMessage.includes("network") ||
		normalizedMessage.includes("fetch failed") ||
		normalizedMessage.includes("load failed") ||
		normalizedMessage.includes("timeout")
	) {
		return true;
	}

	return error instanceof TypeError;
}

// Matches the `[{ code, message }]` shape LtlQuoting/QuoteMgmt send on a 400
// from update-shipment-details.
const updateShipmentDetailsValidationErrorSchema = z.array(
	z.object({
		code: z.string(),
		message: z.string(),
	}),
);

/**
 * Pulls the individual validation messages out of a 400's response body, if
 * it matches the expected shape. Returns null (rather than an empty array)
 * whenever the detail can't be read, so the caller knows to fall back to the
 * plain validation copy instead of showing no detail at all.
 */
async function extractValidationErrorMessages(
	error: unknown,
): Promise<string[] | null> {
	if (!(error instanceof HTTPError)) {
		return null;
	}

	const contentType = error.response.headers.get("content-type") ?? "";
	if (!contentType.toLowerCase().includes("application/json")) {
		return null;
	}

	try {
		const body: unknown = await error.response.clone().json();
		const parsed = updateShipmentDetailsValidationErrorSchema.safeParse(body);
		if (parsed.success && parsed.data.length > 0) {
			return parsed.data.map((validationError) => validationError.message);
		}
	} catch {
		// Not JSON, or didn't match the expected shape - fall back below.
	}

	return null;
}

export async function mapUpdateShipmentDetailsErrorToUserMessage(
	error: unknown,
	consecutiveFailureCount?: number,
): Promise<string> {
	const httpStatus = getHttpStatus(error);

	if (httpStatus === 400) {
		const messages = await extractValidationErrorMessages(error);
		if (messages) {
			return withPersistentFailureGuidance(
				`${UPDATE_SHIPMENT_DETAILS_ERROR_MESSAGES.validation} ${messages.join(" ")}`,
				consecutiveFailureCount,
			);
		}

		return withPersistentFailureGuidance(
			UPDATE_SHIPMENT_DETAILS_ERROR_MESSAGES.validation,
			consecutiveFailureCount,
		);
	}

	if (httpStatus !== null && VALIDATION_STATUSES.has(httpStatus)) {
		return withPersistentFailureGuidance(
			UPDATE_SHIPMENT_DETAILS_ERROR_MESSAGES.validation,
			consecutiveFailureCount,
		);
	}

	if (
		(httpStatus !== null &&
			(SERVICE_UNAVAILABLE_STATUSES.has(httpStatus) || httpStatus >= 500)) ||
		isNetworkOrTransportError(error)
	) {
		return withPersistentFailureGuidance(
			UPDATE_SHIPMENT_DETAILS_ERROR_MESSAGES.serviceUnavailable,
			consecutiveFailureCount,
		);
	}

	return withPersistentFailureGuidance(
		UPDATE_SHIPMENT_DETAILS_ERROR_MESSAGES.fallback,
		consecutiveFailureCount,
	);
}
